
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Watch, Activity, Zap, Moon, Heart, 
  RefreshCw, Link as LinkIcon, AlertCircle, 
  ChevronRight, Smartphone, CheckCircle, Info,
  Flame, Footprints, Navigation
} from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Cell } from 'recharts';
import { User, WearableData } from './types';
import { connectProvider, syncHealthData } from './services/wearableService';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db, auth } from './firebaseConfig';

export const WearableDashboard = ({ user, authReady }: { user: User, authReady: boolean }) => {
  const { t } = useTranslation();
  const [isSyncing, setIsSyncing] = useState(false);
  const [chartData, setChartData] = useState<Array<{ name: string; burned: number; intake: number }>>([]);
  
  const wearable = user.wearable || {
    steps: 0,
    caloriesBurned: 0,
    heartRate: 0,
    activeMinutes: 0,
    sleepHours: 0,
    distance: 0,
    provider: 'manual'
  };

  // Fetch real health history data from Firestore
  useEffect(() => {
    // 🔑 CRITICAL: Block ALL snapshots until auth is ready
    if (!authReady) return;
    
    // 🔒 CRITICAL FIX: Verify user.id === auth.currentUser.uid BEFORE creating listener
    const authUid = auth.currentUser?.uid;
    if (!authUid || authUid !== user.id) {
      if (import.meta.env.DEV) {
        console.warn('[WEARABLE] onSnapshot blocked: user.id !== auth.currentUser.uid');
      }
      setChartData([]);
      return;
    }

    const healthRef = collection(db, "users", user.id, "healthHistory");
    const q = query(healthRef, orderBy("timestamp", "desc"), limit(7));
    
    // CRITICAL: Initialize as null, assign only if onSnapshot returns a function
    let unsubscribe: (() => void) | null = null;
    
    try {
      const snapshotResult = onSnapshot(q, (snap) => {
        const getDayName = (dayIndex: number) => {
          const dayKeys = ['wearable.day_sun', 'wearable.day_mon', 'wearable.day_tue', 'wearable.day_wed', 'wearable.day_thu', 'wearable.day_fri', 'wearable.day_sat'];
          return t(dayKeys[dayIndex] || 'wearable.day_sun');
        };
        const data = snap.docs
          .map((doc, idx) => {
            const healthData = doc.data();
            const date = healthData.date ? new Date(healthData.date) : new Date();
            const dayName = getDayName(date.getDay());
            
            return {
              name: dayName,
              burned: healthData.caloriesBurned || 0,
              intake: healthData.caloriesIntake || 0
            };
          })
          .reverse(); // Reverse to show oldest first
        
        // If we have less than 7 days, pad with zeros
        while (data.length < 7) {
          const dayIndex = data.length;
          data.push({
            name: getDayName(dayIndex % 7),
            burned: 0,
            intake: 0
          });
        }
        
        setChartData(data.slice(-7)); // Take last 7 days
      }, (error) => {
        console.error('[WEARABLE] Error fetching health history:', error);
        // Fallback to empty data if query fails
        setChartData([]);
      });
      if (typeof snapshotResult === 'function') {
        unsubscribe = snapshotResult;
      }
    } catch (initError: any) {
      if (import.meta.env.DEV) {
        console.error('[WEARABLE] Error initializing snapshot listener:', initError);
      }
    }
    
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [authReady, user.id]);

  const handleSync = async () => {
    setIsSyncing(true);
    // Simulate API delay
    setTimeout(async () => {
      await syncHealthData(user.id, {
        steps: Math.floor(Math.random() * 5000) + 5000,
        caloriesBurned: Math.floor(Math.random() * 400) + 300,
        heartRate: 72,
        provider: 'apple'
      });
      setIsSyncing(false);
    }, 1500);
  };

  const steps = wearable.steps ?? 0;
  const goalProgress = (steps / (user.goals?.steps || 10000)) * 100;

  return (
    <div className="p-4 md:p-8 pb-32 space-y-8 max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-3">
            <Watch className="text-emerald-400" size={32} /> {t('wearable.title')}
          </h1>
          <p className="text-slate-500 text-sm font-medium">{t('wearable.subtitle')}</p>
        </div>
        <button 
          onClick={handleSync}
          disabled={isSyncing}
          className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-2xl font-black transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-50"
        >
          <RefreshCw size={18} className={isSyncing ? 'animate-spin' : ''} />
          {isSyncing ? t('wearable.syncing') : t('wearable.sync_now')}
        </button>
      </div>

      {/* Connection Status Section */}
      <section className="grid md:grid-cols-4 gap-4">
        {[
          { id: 'apple', nameKey: 'wearable.provider_apple', icon: <Smartphone/>, color: 'hover:border-slate-400' },
          { id: 'google', nameKey: 'wearable.provider_google', icon: <Activity/>, color: 'hover:border-blue-500' },
          { id: 'fitbit', nameKey: 'wearable.provider_fitbit', icon: <Zap/>, color: 'hover:border-cyan-400' },
          { id: 'garmin', nameKey: 'wearable.provider_garmin', icon: <Navigation/>, color: 'hover:border-blue-600' },
        ].map(p => {
          const isConnected = (wearable.provider || wearable.deviceType) === p.id;
          return (
            <button 
              key={p.id}
              onClick={() => connectProvider(p.id as any)}
              className={`p-4 rounded-3xl border transition-all flex flex-col items-center gap-3 relative overflow-hidden group ${
                isConnected ? 'bg-emerald-500/10 border-emerald-500' : 'bg-slate-900 border-slate-800'
              } ${p.color}`}
            >
              <div className={`${isConnected ? 'text-emerald-500' : 'text-slate-500 group-hover:text-white'}`}>
                {p.icon}
              </div>
              <span className={`text-[10px] font-black uppercase tracking-widest ${isConnected ? 'text-emerald-500' : 'text-slate-500'}`}>
                {t(p.nameKey)}
              </span>
              {isConnected && (
                <CheckCircle size={14} className="absolute top-2 right-2 text-emerald-500" />
              )}
            </button>
          );
        })}
      </section>

      {/* Real-time Stats Grid */}
      <div className="grid md:grid-cols-4 gap-6">
        <StatCard icon={<Footprints className="text-emerald-400"/>} label={t('wearable.steps')} value={steps.toLocaleString()} unit="steps" sub={`${Math.round(goalProgress)}% ${t('wearable.of_goal')}`} />
        <StatCard icon={<Flame className="text-orange-500"/>} label={t('wearable.calories')} value={wearable.caloriesBurned} unit="kcal" sub={t('wearable.active_energy')} />
        <StatCard icon={<Heart className="text-red-500"/>} label={t('wearable.heart_rate')} value={wearable.heartRate} unit="bpm" sub={t('wearable.avg_rest')} />
        <StatCard icon={<Moon className="text-indigo-400"/>} label={t('wearable.sleep')} value={wearable.sleepHours || 0} unit="hours" sub={t('wearable.efficiency')} />
      </div>

      {/* Analytics Section */}
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8 space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="font-black text-xl flex items-center gap-3">{t('wearable.calorie_balance')}</h2>
            <div className="flex gap-4">
              <div className="flex items-center gap-2"><div className="w-3 h-3 bg-emerald-500 rounded-full"/> <span className="text-[10px] font-bold text-slate-500">{t('wearable.out_burned')}</span></div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 bg-indigo-500 rounded-full"/> <span className="text-[10px] font-bold text-slate-500">{t('wearable.in_diet')}</span></div>
            </div>
          </div>
          
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData.length > 0 ? chartData : [{ name: t('wearable.no_data'), burned: 0, intake: 0 }]}>
                <defs>
                  <linearGradient id="colorBurn" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorIntake" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '16px' }}
                  itemStyle={{ fontWeight: 'bold' }}
                />
                <Area type="monotone" dataKey="burned" stroke="#10b981" fillOpacity={1} fill="url(#colorBurn)" strokeWidth={3} />
                <Area type="monotone" dataKey="intake" stroke="#6366f1" fillOpacity={1} fill="url(#colorIntake)" strokeWidth={3} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8 space-y-8 flex flex-col justify-between">
          <h2 className="font-black text-xl">{t('wearable.activity_goals')}</h2>
          
          <div className="space-y-8">
            <GoalProgress label={t('wearable.steps_label')} current={steps} target={10000} color="bg-emerald-500" />
            <GoalProgress label={t('wearable.active_minutes')} current={45} target={60} color="bg-cyan-400" />
            <GoalProgress label={t('wearable.calories_burned')} current={wearable.caloriesBurned} target={600} color="bg-orange-500" />
          </div>

          <div className="bg-slate-800/50 p-6 rounded-3xl border border-slate-700/50 flex gap-4">
            <div className="p-3 bg-indigo-500/20 text-indigo-400 rounded-2xl h-fit">
              <Info size={24}/>
            </div>
            <div>
              <p className="text-sm font-bold mb-1">{t('wearable.coach_tip')}</p>
              <p className="text-xs text-slate-400 leading-relaxed">{t('wearable.coach_tip_text')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, unit, sub }: any) => (
  <div className="bg-slate-900 border border-slate-800 p-6 rounded-[2rem] hover:border-emerald-500/30 transition-all group">
    <div className="flex justify-between items-start mb-4">
      <div className="p-3 bg-slate-800 rounded-2xl group-hover:scale-110 transition-transform">{icon}</div>
      <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{label}</div>
    </div>
    <div className="flex items-baseline gap-1">
      <span className="text-3xl font-black text-white">{value}</span>
      <span className="text-sm font-bold text-slate-500 uppercase">{unit}</span>
    </div>
    <p className="text-[10px] font-bold text-emerald-400 mt-2 uppercase tracking-tighter">{sub}</p>
  </div>
);

const GoalProgress = ({ label, current, target, color }: any) => {
  const percent = Math.min((current / target) * 100, 100);
  return (
    <div className="space-y-2">
      <div className="flex justify-between text-xs font-bold uppercase tracking-widest">
        <span className="text-slate-400">{label}</span>
        <span className="text-white">{current} / {target}</span>
      </div>
      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all duration-1000`} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}
